import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ExternalLink, FileText, CheckCircle } from "lucide-react";

const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfDBVxYesP9j5r0Mz0K1YeBUIfFIZLAFEdJUgemHusOyBPbeg/viewform";

const SIZES = [
  { size: '5×7"', price: "$20", description: "Perfect for small spaces or gifts" },
  { size: '8×10"', price: "$35", description: "Most popular size" },
  { size: '12×16"', price: "$50", description: "Larger statement piece" },
  { size: "Portraits", price: "$85+", description: "Starting price, varies by detail" },
];

const QuotePage = () => {
  return (
    <div data-testid="quote-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-copper text-sm tracking-[0.3em] uppercase mb-4 font-body">Get Started</p>
          <h1 
            data-testid="quote-title"
            className="font-heading text-4xl md:text-6xl mb-6"
          >
            Order <span className="copper-text">Request</span>
          </h1>
          <p className="font-body text-gray-400 max-w-2xl mx-auto">
            Each piece is a mosaic of tiny copper pieces, hand-painted with oil-based markers 
            and mounted on glass, creating vibrant artwork that captures light and color.
          </p>
        </div>

        {/* Pricing Info */}
        <div className="bg-white/[0.02] border border-white/5 p-8 mb-12">
          <h2 className="font-heading text-2xl mb-6 text-center">
            <span className="copper-text">Pricing</span>
          </h2>
          <p className="font-body text-gray-400 text-center mb-8 text-sm">
            Prices include frame. Final cost depends on image detail and complexity.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SIZES.map((item) => (
              <div 
                key={item.size}
                className="border border-white/10 p-4 text-center hover:border-copper/30 transition-colors"
              >
                <p className="font-heading text-2xl copper-text mb-1">{item.price}</p>
                <p className="font-body text-white text-sm mb-1">{item.size}</p>
                <p className="font-body text-gray-500 text-xs">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white/[0.02] border border-white/5 p-8 mb-12">
          <h2 className="font-heading text-2xl mb-6 text-center">
            How It <span className="copper-text">Works</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <span className="font-heading text-copper text-xl">1</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Submit Request</h3>
              <p className="font-body text-gray-400 text-sm">
                Fill out the order form with your image and contact info
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <span className="font-heading text-copper text-xl">2</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Review & Quote</h3>
              <p className="font-body text-gray-400 text-sm">
                I'll contact you to discuss details and provide a final quote
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <span className="font-heading text-copper text-xl">3</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Creation & Delivery</h3>
              <p className="font-body text-gray-400 text-sm">
                Your custom piece is created and shipped to you
              </p>
            </div>
          </div>
        </div>

        {/* What You'll Need */}
        <div className="bg-white/[0.02] border border-white/5 p-8 mb-12">
          <h2 className="font-heading text-xl mb-4">What You'll Need:</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-copper mt-0.5 flex-shrink-0" />
              <span className="font-body text-gray-300 text-sm">
                <strong className="text-white">Image or logo</strong> - Describe what you want created
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-copper mt-0.5 flex-shrink-0" />
              <span className="font-body text-gray-300 text-sm">
                <strong className="text-white">Image link</strong> - Paste a link to your image (Google Drive, Imgur, etc.)
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-copper mt-0.5 flex-shrink-0" />
              <span className="font-body text-gray-300 text-sm">
                <strong className="text-white">Contact info</strong> - Discord, Email, or Phone number
              </span>
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a 
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-form-btn"
          >
            <Button
              className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-10 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(184,115,51,0.4)] flex items-center gap-2 mx-auto"
            >
              <FileText size={18} />
              Open Order Request Form
              <ExternalLink size={16} />
            </Button>
          </a>
          
          <p className="font-body text-gray-500 text-xs mt-4">
            Opens Google Forms in a new tab
          </p>
        </div>

        {/* Note */}
        <div className="mt-12 p-6 border border-copper/20 bg-copper/5 text-center">
          <p className="font-body text-gray-300 text-sm">
            <strong className="text-copper">Note:</strong> After you submit the form, I will contact you 
            to go over details and availability before the order is completed. Shipping cost will be 
            determined after the art is made and you are satisfied with the piece.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotePage;
